import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import OncolensToast from '@/components/OncolensToast';
import { AnnouncementsTextArea } from '../components/AnnouncementsTextArea';
import { Button } from '@/components/v2/Button';
import { Conference, useGetSingleConference, useUpdateConference } from '../api/conferences';
import { ConferenceNameInput } from '../components/ConferenceNameInput';
import { Form, FormItem, FormLabel } from '@/components/v2/Form/Form';
import { Input } from '@/components/v2/Form/Input';
import {
  ConferenceVirtualSettings,
  hasValidUrls,
  isConferenceVirtualSettingsDirty,
  OrgVirtualSettingsDefaults,
  ConferenceVirtualSettingsValues,
} from '../components/ConferenceVirtualSettings';
import {
  ConferenceVirtualSettingsDto,
  ConferenceVirtualSettingsWithContextDto,
  useGetConferenceVirtualSettings,
  useUpsertConferenceVirtualSettings,
} from '../api/conferenceVirtualSettings';

const formSchema = z.object({
  conferenceName: z.string(),
  hostOrg: z.string().optional(),
  announcements: z.string().optional(),
});

const defaultSettingsValues: ConferenceVirtualSettingsValues = {
  infoPanelEnabled: false,
  audioRecording: false,
  videoRecording: false,
  joinUrl: '',
  startUrl: '',
};

function resolveConferenceVirtualSettings(
  context: ConferenceVirtualSettingsWithContextDto,
  settings: ConferenceVirtualSettingsDto | null,
): ConferenceVirtualSettingsValues {
  if (!context.hasVirtualConferenceLicense) return defaultSettingsValues;

  return {
    infoPanelEnabled:
      settings?.virtualConferenceInfoPanelEnabled ??
      context.orgDefaults.virtualConferenceInfoPanelEnabled ??
      false,
    audioRecording:
      settings?.virtualConferenceAudioRecording ??
      context.orgDefaults.virtualConferenceAudioRecording ??
      false,
    videoRecording: settings?.virtualConferenceVideoRecording ?? false,
    joinUrl: settings?.virtualConferenceJoinUrl ?? '',
    startUrl: settings?.virtualConferenceStartUrl ?? '',
  };
}

export const EditConference = ({
  setIsEditConference,
  conference,
}: {
  setIsEditConference: (value: boolean) => void;
  conference: Conference;
}) => {
  const { data } = useGetSingleConference(conference?.id || '');

  const { data: settingsContext, isFetched: settingsFetched } = useGetConferenceVirtualSettings(
    conference?.id,
  );

  const hasVirtualConferenceLicense = settingsContext?.hasVirtualConferenceLicense ?? false;
  const orgDefaults: OrgVirtualSettingsDefaults | undefined = settingsContext
    ? {
        infoPanelEnabled: settingsContext.orgDefaults.virtualConferenceInfoPanelEnabled ?? false,
        audioRecording: settingsContext.orgDefaults.virtualConferenceAudioRecording ?? false,
      }
    : undefined;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      conferenceName: '',
      announcements: '',
    },
  });

  const [settingsValues, setSettingsValues] =
    useState<ConferenceVirtualSettingsValues>(defaultSettingsValues);
  const loadedSettingsValues = useRef<ConferenceVirtualSettingsValues>(defaultSettingsValues);
  const lastSettingsFingerprint = useRef('');

  useEffect(() => {
    if (data) {
      form.setValue('conferenceName', data.name);
      form.setValue('announcements', data.announcement);
    }
  }, [data, form]);

  // Re-initialize virtual conference settings when server data changes
  useEffect(() => {
    if (!settingsFetched || !settingsContext) return;

    const settings = settingsContext.settings ?? null;
    const fingerprint = JSON.stringify({
      settingsId: settings?.id ?? null,
      orgDefaults: settingsContext.orgDefaults,
      licensed: settingsContext.hasVirtualConferenceLicense,
    });

    if (fingerprint === lastSettingsFingerprint.current) return;
    lastSettingsFingerprint.current = fingerprint;

    const values = resolveConferenceVirtualSettings(settingsContext, settings);
    setSettingsValues(values);
    loadedSettingsValues.current = values;
  }, [settingsFetched, settingsContext]);

  const updateConference = useUpdateConference(conference?.id || '');
  const upsertSettings = useUpsertConferenceVirtualSettings(conference?.id || '');

  const handleSave = async () => {
    const formValues = form.getValues();

    try {
      const conferencePromise = updateConference.mutateAsync({
        name: formValues.conferenceName,
        announcement: formValues.announcements || '',
      });

      const settingsPromise =
        hasVirtualConferenceLicense &&
        isConferenceVirtualSettingsDirty(settingsValues, loadedSettingsValues.current)
          ? upsertSettings.mutateAsync({
              virtualConferenceInfoPanelEnabled: settingsValues.infoPanelEnabled,
              virtualConferenceAudioRecording: settingsValues.audioRecording,
              virtualConferenceVideoRecording: settingsValues.videoRecording,
              virtualConferenceJoinUrl: settingsValues.joinUrl || null,
              virtualConferenceStartUrl: settingsValues.startUrl || null,
            })
          : Promise.resolve();

      await Promise.all([conferencePromise, settingsPromise]);
      setIsEditConference(false);
      OncolensToast.success('Conference updated successfully');
    } catch {
      OncolensToast.error('Error updating conference');
    }
  };

  const isFormDirty = () => {
    if (!data) return false;
    const formValues = form.getValues();
    const overviewDirty =
      formValues.conferenceName !== data.name || formValues.announcements !== data.announcement;
    return (
      overviewDirty ||
      isConferenceVirtualSettingsDirty(settingsValues, loadedSettingsValues.current)
    );
  };

  return (
    <>
      <div className="mt-12 px-8 flex justify-between items-end">
        <div className="flex flex-col text-zinc-800 items-start">
          <Button
            variant="link"
            testId="go-back-button"
            onClickHandler={() => setIsEditConference(false)}>
            Go back
          </Button>
          <h1 className="text-2xl font-semibold">Edit a conference</h1>
        </div>
        <div className="flex gap-4">
          <Button
            variant="link"
            testId="cancel-conference-button"
            onClickHandler={() => setIsEditConference(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!isFormDirty() || !hasValidUrls(settingsValues)}
            testId="save-conference-button"
            onClickHandler={() => handleSave()}>
            Save conference
          </Button>
        </div>
      </div>
      <div className="w-full px-8 pb-20">
        <Form {...form}>
          <div className="bg-white shadow-sm border rounded-lg w-full mt-6">
            <div className="border-b-[#E4E4E7] border-b">
              <p className="font-bold text-sm text-zinc-800 px-6 py-2">Overview</p>
            </div>
            <div className="py-8 px-6">
              <form data-cy="edit-conference-form" className="md:flex w-full gap-6">
                <div className="w-full max-w-xs flex flex-col gap-5 mt-[-4px]">
                  <ConferenceNameInput control={form.control} name="conferenceName" />
                  <FormItem>
                    <FormLabel>Parent organization</FormLabel>
                    <Input
                      data-cy="parent-org-input"
                      defaultValue={conference?.parentOrg}
                      disabled
                    />
                  </FormItem>
                  <FormItem>
                    <FormLabel>Host organization</FormLabel>
                    <Input data-cy="host-org-input" defaultValue={conference?.hostOrg} disabled />
                  </FormItem>
                </div>
                <div className="mt-6 w-full h-full md:mt-[-3px]">
                  <AnnouncementsTextArea control={form.control} name="announcements" />
                </div>
              </form>
            </div>
          </div>
          <ConferenceVirtualSettings
            values={settingsValues}
            onChange={setSettingsValues}
            disabled={!hasVirtualConferenceLicense}
            orgDefaults={orgDefaults}
          />
        </Form>
      </div>
    </>
  );
};
